import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Users, Trophy, TrendingUp, TrendingDown, ChevronRight, BarChart2, Database, Coins, FileText, MessageSquare, Tag, Bug, Shield, Trash2, Mic, RefreshCw, CreditCard, Loader as Loader2, Minus, Palette } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

import type { DashboardStats } from '../services/AdminService';

const defaultStats: DashboardStats = {
  totalUsers: 0,
  newUsersThisMonth: 0,
  newUsersLastMonth: 0,
  totalSongs: 0,
  activeTournaments: 0,
  totalPerformances: 0,
  totalRecordings: 0,
  averageScore: 0,
  activeSubscriptions: 0,
  cancelledSubscriptions: 0,
  subscriptionsByType: [],
  revenueFromPurchases: 0,
  revenueFromSubscriptions: 0,
  userSignupsByMonth: [],
  recordingsByMonth: [],
  transactionsByType: [],
};

export const AdminDashboard: React.FC = () => {
  const { adminService } = useData();
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await adminService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const userGrowthPercent = stats.newUsersLastMonth > 0
    ? Math.round(((stats.newUsersThisMonth - stats.newUsersLastMonth) / stats.newUsersLastMonth) * 100)
    : stats.newUsersThisMonth > 0 ? 100 : 0;

  const chartBase = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(255, 255, 255, 0.06)' },
        ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 11 } },
      },
      x: {
        grid: { display: false },
        ticks: { color: 'rgba(255, 255, 255, 0.5)', font: { size: 11 } },
      },
    },
  };

  const signupChartData = {
    labels: stats.userSignupsByMonth.map(d => d.month),
    datasets: [{
      label: 'Signups',
      data: stats.userSignupsByMonth.map(d => d.count),
      fill: true,
      borderColor: '#14b8a6',
      backgroundColor: 'rgba(20, 184, 166, 0.1)',
      tension: 0.4,
      pointBackgroundColor: '#14b8a6',
      pointRadius: 4,
    }],
  };

  const recordingChartData = {
    labels: stats.recordingsByMonth.map(d => d.month),
    datasets: [{
      label: 'Recordings',
      data: stats.recordingsByMonth.map(d => d.count),
      backgroundColor: 'rgba(20, 184, 166, 0.7)',
      borderRadius: 6,
      barThickness: 28,
    }],
  };

  const subTypeColors: Record<string, string> = {
    weekly: '#f59e0b',
    monthly: '#14b8a6',
    annual: '#3b82f6',
    yearly: '#3b82f6',
  };

  const subDonutData = {
    labels: stats.subscriptionsByType.map(s => s.plan_type.charAt(0).toUpperCase() + s.plan_type.slice(1)),
    datasets: [{
      data: stats.subscriptionsByType.map(s => s.count),
      backgroundColor: stats.subscriptionsByType.map(s => subTypeColors[s.plan_type] || '#6b7280'),
      borderWidth: 0,
      hoverOffset: 6,
    }],
  };

  const txTypeLabels: Record<string, string> = {
    purchase: 'Purchases',
    subscription: 'Subscriptions',
    admin_gift: 'Admin Gifts',
    reward: 'Rewards',
    tournament_entry: 'Tournament Entries',
    tournament_win: 'Tournament Wins',
    admin_deduction: 'Admin Deductions',
    performance_scoring: 'Performance Rewards',
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-teal-400 mx-auto mb-4" />
          <p className="text-white/50">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total Users"
          value={stats.totalUsers.toLocaleString()}
          icon={<Users size={22} className="text-teal-400" />}
          trend={userGrowthPercent}
          trendLabel="vs last month"
          sub={`${stats.newUsersThisMonth} new this month`}
        />
        <StatCard
          label="Active Subscriptions"
          value={stats.activeSubscriptions.toLocaleString()}
          icon={<CreditCard size={22} className="text-teal-400" />}
          sub={`${stats.cancelledSubscriptions} cancelled`}
        />
        <StatCard
          label="Total Recordings"
          value={stats.totalRecordings.toLocaleString()}
          icon={<Mic size={22} className="text-teal-400" />}
          sub={`${stats.totalPerformances} performances`}
        />
        <StatCard
          label="Active Tournaments"
          value={stats.activeTournaments.toLocaleString()}
          icon={<Trophy size={22} className="text-teal-400" />}
          sub={`${stats.totalSongs} songs in catalog`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 card">
          <h3 className="text-lg font-semibold mb-1">User Signups</h3>
          <p className="text-white/40 text-sm mb-4">Last 6 months</p>
          {stats.userSignupsByMonth.length > 0 ? (
            <Line data={signupChartData} options={chartBase as any} />
          ) : (
            <p className="text-white/30 text-center py-10">No signup data available</p>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-1">Subscriptions by Plan</h3>
          <p className="text-white/40 text-sm mb-4">Active subscribers</p>
          {stats.subscriptionsByType.length > 0 ? (
            <div className="flex flex-col items-center">
              <div className="w-48 h-48">
                <Doughnut
                  data={subDonutData}
                  options={{
                    responsive: true,
                    cutout: '65%',
                    plugins: { legend: { display: false } },
                  }}
                />
              </div>
              <div className="mt-4 space-y-2 w-full">
                {stats.subscriptionsByType.map(s => (
                  <div key={s.plan_type} className="flex items-center justify-between px-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full inline-block"
                        style={{ backgroundColor: subTypeColors[s.plan_type] || '#6b7280' }}
                      />
                      <span className="text-sm text-white/70 capitalize">{s.plan_type}</span>
                    </div>
                    <span className="text-sm font-semibold">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-white/30">
              <RefreshCw size={28} className="mb-3 opacity-50" />
              <p>No active subscriptions</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <h3 className="text-lg font-semibold mb-1">Recordings</h3>
          <p className="text-white/40 text-sm mb-4">Last 6 months</p>
          {stats.recordingsByMonth.length > 0 ? (
            <Bar data={recordingChartData} options={chartBase as any} />
          ) : (
            <p className="text-white/30 text-center py-10">No recording data</p>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-1">Token Economy</h3>
          <p className="text-white/40 text-sm mb-4">All-time transaction breakdown</p>
          {stats.transactionsByType.length > 0 ? (
            <div className="space-y-3">
              {stats.transactionsByType
                .sort((a, b) => b.total - a.total)
                .map(tx => {
                  const maxTotal = Math.max(...stats.transactionsByType.map(t => t.total));
                  const pct = maxTotal > 0 ? (tx.total / maxTotal) * 100 : 0;
                  return (
                    <div key={tx.type}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-white/70">{txTypeLabels[tx.type] || tx.type}</span>
                        <span className="text-sm font-medium">{Math.round(tx.total).toLocaleString()} tokens <span className="text-white/40">({tx.count})</span></span>
                      </div>
                      <div className="w-full bg-white/5 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-teal-500/70"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-white/30 text-center py-10">No transaction data</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <QuickLink to="/admin/songs" icon={<Database size={24} />} title="Song Management" desc="Manage your song catalog" />
        <QuickLink to="/admin/coins" icon={<Coins size={24} />} title="Virtual Currency" desc="Manage virtual coins and transactions" />
        <QuickLink to="/admin/tournaments" icon={<Trophy size={24} />} title="Tournament Management" desc="Manage active tournaments" />
        <QuickLink to="/admin/achievements" icon={<Trophy size={24} />} title="Achievement Management" desc="Manage achievements and rewards" />
        <QuickLink to="/admin/categories" icon={<Tag size={24} />} title="Category Management" desc="Manage tournament categories" />
        <QuickLink to="/admin/users" icon={<Users size={24} />} title="User Management" desc="Manage user accounts" />
        <QuickLink to="/technical/debug" icon={<Bug size={24} />} title="Technical Debug" desc="Performance analysis debugging" />
        <QuickLink to="/admin/privacy-policy" icon={<Shield size={24} />} title="Privacy Policy" desc="Edit privacy policy content" />
        <QuickLink to="/admin/data-deletion" icon={<Trash2 size={24} />} title="Data Deletion" desc="Edit data deletion instructions" />
        <QuickLink to="/admin/referrals" icon={<Users size={24} />} title="Referral Program" desc="Configure rewards and view referral activity" />
        <QuickLink to="/admin/kiosk-leagues" icon={<Trophy size={24} />} title="Kiosk Leagues" desc="White-label singing leagues for external clients" />
        <QuickLink to="/admin/sing-along-leagues" icon={<Mic size={24} />} title="Sing-Along Leagues" desc="Family and friends sing-along events" />
        <QuickLink to="/admin/sing-along-bundles" icon={<Mic size={24} />} title="Sing-Along Bundles" desc="Manage song bundles for consumer kiosk passes" />
        <QuickLink to="/admin/sing-along-branding" icon={<Palette size={24} />} title="Email Branding" desc="Sponsor logos and colors for verification emails" />
        <QuickLink to="/admin/community" icon={<MessageSquare size={24} />} title="Community Management" desc="Manage user comments and posts" />
        <QuickLink to="/admin/reports" icon={<BarChart2 size={24} />} title="Reports" desc="View detailed analytics" />
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  trend?: number;
  trendLabel?: string;
  sub?: string;
}> = ({ label, value, icon, trend, trendLabel, sub }) => {
  const isPositive = trend !== undefined && trend >= 0;
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-white/50 text-sm mb-1">{label}</p>
          <h3 className="text-2xl font-bold">{value}</h3>
        </div>
        <div className="bg-kod-purple-700 p-3 rounded-lg">{icon}</div>
      </div>
      <div className="mt-3 flex items-center gap-2 text-sm flex-wrap">
        {trend !== undefined && (
          <span className={`flex items-center font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {isPositive ? <TrendingUp size={14} className="mr-0.5" /> : <TrendingDown size={14} className="mr-0.5" />}
            {trend > 0 ? '+' : ''}{trend}%
            {trendLabel && <span className="text-white/40 font-normal ml-1">{trendLabel}</span>}
          </span>
        )}
        {sub && <span className="text-white/40">{trend !== undefined ? '|' : ''} {sub}</span>}
      </div>
    </div>
  );
};

const QuickLink: React.FC<{
  to: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
}> = ({ to, icon, title, desc }) => (
  <Link to={to} className="card hover:bg-kod-purple-700/50 transition-colors group">
    <div className="flex items-center justify-between mb-4">
      <div className="bg-kod-purple-700 p-3 rounded-lg text-teal-400">{icon}</div>
      <ChevronRight size={20} className="text-white/50 group-hover:text-white/70" />
    </div>
    <h3 className="font-semibold mb-1">{title}</h3>
    <p className="text-sm text-white/70">{desc}</p>
  </Link>
);
